import React, { useState, useEffect } from "react";
import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import DefaultButton from "../../../components/buttons/defaultButton";
import DefaultInput from "../../../components/inputs/defaultInput";
import DefaultSearchSelectInput from "../../../components/inputs/defaultSearchSelectInput";
import CommonModal from "../../../components/modals/commonModel";
import {
  getExpenseCategories,
  createExpenseCategory,
  updateExpenseCategory,
  deleteExpenseCategory,
} from "../../../../services/workspace/reimbursement.service";
import { toast } from "react-hot-toast";
import { FiPlus, FiEdit2, FiTrash2, FiCheck, FiX } from "react-icons/fi";
import DefaultBadge from "../../../components/badge/defaultBadge";

const items = [
  { label: "Reimbursement", path: "/reimbursement" },
  { label: "Category Management", path: "" },
];

const CategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    limit_per_transaction: "",
    requires_receipt: true,
    is_active: true,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getExpenseCategories();
      setCategories(response.data || []);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to fetch categories:", error);
      }
      toast.error("Failed to load categories. Please refresh the page.");
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name || "",
        description: category.description || "",
        limit_per_transaction: category.limit_per_transaction || "",
        requires_receipt: category.requires_receipt !== false,
        is_active: category.is_active !== false,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: "",
        description: "",
        limit_per_transaction: "",
        requires_receipt: true,
        is_active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    if (submitting) return; // Prevent closing while submitting
    setIsModalOpen(false);
    setEditingCategory(null);
    setFormData({
      name: "",
      description: "",
      limit_per_transaction: "",
      requires_receipt: true,
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (submitting) return;

    // Validation
    if (!formData.name.trim()) {
      toast.error("Category name is required");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        limit_per_transaction: formData.limit_per_transaction
          ? parseFloat(formData.limit_per_transaction)
          : null,
        requires_receipt: formData.requires_receipt,
        is_active: formData.is_active,
      };

      if (process.env.NODE_ENV === 'development') {
      }

      let response;
      if (editingCategory) {
        response = await updateExpenseCategory(editingCategory.id, payload);
        if (process.env.NODE_ENV === 'development') {
        }
        toast.success("Category updated successfully");
      } else {
        response = await createExpenseCategory(payload);
        if (process.env.NODE_ENV === 'development') {
        }
        toast.success("Category created successfully");
      }

      handleCloseModal();
      await fetchCategories();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to save category:", error);
        console.error("Error response:", error.response);
        console.error("Error data:", error.response?.data);
      }
      
      // Better error message
      const errorMessage = 
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        `Failed to ${editingCategory ? "update" : "create"} category`;
      
      toast.error(errorMessage);
      
      // If it's a network error or 404, suggest backend might not be ready
      if (error.response?.status === 404 || error.code === 'ERR_NETWORK') {
        if (process.env.NODE_ENV === 'development') {
          console.warn("API endpoint might not exist. Backend needs to implement this endpoint.");
        }
        toast.error("API endpoint not found. Please check if backend is running and endpoint is implemented.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (category) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${category.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await deleteExpenseCategory(category.id);
      toast.success("Category deleted successfully");
      fetchCategories();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error("Failed to delete category:", error);
      }
      toast.error(
        error.response?.data?.message || "Failed to delete category"
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageTableHeader breadcrumbs={items} />

      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Expense Categories
          </h1>
          <p className="text-gray-600 mt-1">
            Manage expense categories for reimbursement
          </p>
        </div>
        <DefaultButton
          name="Add Category"
          variant="primary"
          onClick={() => handleOpenModal()}
          icon={<FiPlus />}
        />
      </div>

      {/* Categories Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        {categories.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 mb-4">No categories found</p>
            <DefaultButton
              name="Create First Category"
              variant="primary"
              onClick={() => handleOpenModal()}
              icon={<FiPlus />}
            />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Limit per Transaction
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receipt Required
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {category.name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-600">
                        {category.description || "-"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {category.limit_per_transaction
                          ? `₹${category.limit_per_transaction.toLocaleString()}`
                          : "No limit"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {category.requires_receipt !== false ? (
                        <DefaultBadge status={1} custom="Required" />
                      ) : (
                        <DefaultBadge status={0} custom="Optional" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {category.is_active !== false ? (
                        <DefaultBadge status={1} custom="Active" />
                      ) : (
                        <DefaultBadge status={2} custom="Inactive" />
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(category)}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          onClick={() => handleDelete(category)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      <CommonModal
        show={isModalOpen}
        onClose={handleCloseModal}
        headerTitle={editingCategory ? "Edit Category" : "Create New Category"}
        modalSize="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <DefaultInput
            label="Category Name"
            name="name"
            value={formData.name}
            onChange={(e) =>
              setFormData({ ...formData, name: e.target.value })
            }
            placeholder="e.g., Travel, Food, Office Supplies"
            required
          />

          <DefaultInput
            label="Description"
            name="description"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            placeholder="Brief description of this category"
          />

          <DefaultInput
            label="Limit per Transaction (₹)"
            name="limit_per_transaction"
            type="number"
            value={formData.limit_per_transaction}
            onChange={(e) =>
              setFormData({
                ...formData,
                limit_per_transaction: e.target.value,
              })
            }
            placeholder="Leave empty for no limit"
            min="0"
            step="0.01"
          />

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.requires_receipt}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    requires_receipt: e.target.checked,
                  })
                }
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                Receipt Required
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) =>
                  setFormData({ ...formData, is_active: e.target.checked })
                }
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Active</span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <DefaultButton
              name="Cancel"
              variant="outline"
              onClick={handleCloseModal}
              type="button"
              disabled={submitting}
            />
            <DefaultButton
              name={submitting ? (editingCategory ? "Updating..." : "Creating...") : (editingCategory ? "Update" : "Create")}
              variant="primary"
              type="submit"
              icon={editingCategory ? <FiCheck /> : <FiPlus />}
              disabled={submitting}
            />
          </div>
        </form>
      </CommonModal>
    </div>
  );
};

export default CategoryManagement;

